import { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import { Typography, Flex, RemoteImage, List } from '@banhala/ui-kit';
import type { MarketSmallBadgeListProps, MarketSmallBadgeItem } from './types';
import type { AnyObject } from '../../../../types';
import { isNil } from '../../../../utils';
import { useOpenDeeplink, useLogAnalyticsEvent } from '../../../../hooks';
import { useLogDisplayCarousel } from './hooks/useLogDisplayCarousel';

const PADDING_HORIZONTAL = 16;
const COLUMN_GAP = 8;
const ROW_GAP = 8;
const ICON_SIZE = 32;
const ITEM_WIDTH = 140;
const ITEM_HEIGHT = ICON_SIZE + 4;

type BadgeEntity = { item: MarketSmallBadgeItem; logging: AnyObject };
type ColumnGroup = BadgeEntity[];

interface MarketSmallBadgeShortIconProps {
  item: MarketSmallBadgeItem;
  logging: AnyObject;
}

const MarketSmallBadgeShortIcon = ({
  item,
  logging,
}: MarketSmallBadgeShortIconProps) => {
  const { name, badge, deeplink } = item;
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePress = useCallback(() => {
    if (deeplink) {
      logAnalyticsEvent({
        name: 'CLICK_STORE',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging, logAnalyticsEvent]);

  return (
    <Flex.Row
      width={ITEM_WIDTH}
      height={ITEM_HEIGHT}
      alignItems="center"
      onPress={handlePress}
    >
      <Flex
        width={ICON_SIZE}
        height={ICON_SIZE}
        borderRadius={ICON_SIZE / 2}
        borderWidth={1}
        borderColor="border_transparent"
        overflow="hidden"
      >
        <RemoteImage uri={badge} width={ICON_SIZE} height={ICON_SIZE} />
      </Flex>

      <Flex flex={1} margin={{ left: 6 }}>
        <Typography type="subtitle2" color="content_primary" numberOfLines={1}>
          {name}
        </Typography>
      </Flex>
    </Flex.Row>
  );
};

const ColumnItem = ({ column }: { column: ColumnGroup }) => (
  <Flex gap={ROW_GAP}>
    {column.map(entity => (
      <MarketSmallBadgeShortIcon
        key={entity.item.sno}
        item={entity.item}
        logging={entity.logging}
      />
    ))}
  </Flex>
);

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (_: ColumnGroup, index: number) => `column-${index}`;

const renderItem = ({ item }: { item: ColumnGroup }) => (
  <ColumnItem column={item} />
);

export const MarketSmallBadgeList = ({
  entity,
  body,
  logging,
  isVisible,
  isAwaitingDataChange,
}: MarketSmallBadgeListProps) => {
  const row = body?.row ?? 3;

  const logAnalyticsEvent = useLogAnalyticsEvent();
  // GoodsCarousel 과 동일한 타이밍 — 첫 viewable 아이템 보고 시 DISPLAY_CAROUSEL 을
  // DISPLAY_STORE 보다 먼저 동기 호출. logged.current 로 dedup, parent isVisible
  // 가드로 viewport 밖 발화 차단.
  const { logDisplayCarousel } = useLogDisplayCarousel(
    logging,
    isVisible,
    isAwaitingDataChange,
  );

  const columns = useMemo(() => {
    if (isNil(entity) || entity.length === 0) return [];
    const result: ColumnGroup[] = [];
    for (let i = 0; i < entity.length; i += row) {
      result.push(entity.slice(i, i + row));
    }
    return result;
  }, [entity, row]);

  // GoodsCarousel.useLogDisplayGoods 패턴 — inner FlatList 가 부모 viewport 와
  // 무관하게 viewable 을 보고하므로, emit 시점에 부모 가시성으로 가드 +
  // isVisible false→true 전이 시 캐시 기반 재emit 으로 노출 결손 방지.
  const loggedSnosRef = useRef<Set<number>>(new Set());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (isVisibleRef.current === false) return;
      if (isAwaitingDataChangeRef.current === true) return;
      if (viewableItems.some(v => v.isViewable)) {
        logDisplayCarousel();
      }
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable) return;
        const columnData = item as ColumnGroup;
        columnData.forEach(badgeData => {
          const sno = badgeData.item.sno;
          if (loggedSnosRef.current.has(sno)) return;
          loggedSnosRef.current.add(sno);
          logAnalyticsEvent({
            channel: ['ablylog', 'firebase'],
            name: 'DISPLAY_STORE',
            analytics: badgeData.logging.analytics,
            ably_only_log: badgeData.logging.ably_only_log,
          });
        });
      });
    },
    [logAnalyticsEvent, logDisplayCarousel],
  );

  useEffect(() => {
    if (isVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  if (columns.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={columns}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: COLUMN_GAP,
    paddingBottom: 16,
  },
});
